// BOORO: the knowledge of a space. Pages of Markdown that link to each other,
// so the handbook and what the company knows read like notes, not like a chat.

/** The kinds of knowledge a space holds. */
export const KNOWLEDGE_KINDS = ["handbuch", "firmenwissen"] as const;
export type KnowledgeKind = (typeof KNOWLEDGE_KINDS)[number];

/** One page of the knowledge. */
export type KnowledgePage = {
  id: string;
  /** The office of the page; null for the knowledge of the whole account. */
  spaceId: string | null;
  kind: KnowledgeKind;
  title: string;
  /** The address of the page, unique in the space. */
  path: string;
  /** The folder the page lies in, as "Marke/Logos"; empty at the top. */
  folder: string;
  /** One sentence on what the page holds; empty when nobody wrote one. */
  summary: string;
  content: string;
  createdByUserId: string;
  /** The agent that wrote the page last; null when a person did. */
  updatedByBotId: string | null;
  createdAt: string;
  updatedAt: string;
};

/** The words a link uses: `[[Title]]` or `[Title](path)`. */
const WIKI_LINK = /\[\[([^\]|]+)(?:\|[^\]]+)?\]\]/g;
const MARKDOWN_LINK = /\[[^\]]*\]\(([^)\s]+)\)/g;

/** The pages a text links to, by title. */
export function parsePageLinks(content: string): string[] {
  const titles: string[] = [];
  for (const match of content.matchAll(WIKI_LINK)) {
    const title = match[1]?.trim();
    if (title && !titles.includes(title)) titles.push(title);
  }
  for (const match of content.matchAll(MARKDOWN_LINK)) {
    const path = match[1]?.trim();
    // Only links that point at a page of the knowledge count.
    if (path?.startsWith("wissen:") && !titles.includes(path)) titles.push(path);
  }
  return titles;
}

/**
 * Rewrites the wiki links into markdown links that point at the page. A link
 * whose target does not exist stays as it is, so the mistake stays visible.
 */
export function resolvePageLinks(
  content: string,
  pages: readonly { title: string; path: string }[],
): string {
  const byTitle = new Map(pages.map((page) => [page.title.toLowerCase(), page.path]));
  return content.replace(WIKI_LINK, (whole, title: string) => {
    const path = byTitle.get(title.trim().toLowerCase());
    if (!path) return whole;
    return `[${title.trim()}](wissen:${path})`;
  });
}

const PAGE_HREF = /\]\((wissen:[^)\s]+)\)/g;

/** Gives every link to a page another href, so a view can open the page itself. */
export function mapPageLinks(content: string, href: (link: string) => string): string {
  return content.replace(PAGE_HREF, (_whole, link: string) => `](${href(link)})`);
}

/**
 * The page a link points at, by title or as `wissen:` and its address. A page
 * of the same place comes before one of the whole account.
 */
export function linkedPage<T extends { title: string; path: string; spaceId: string | null }>(
  link: string,
  pages: readonly T[],
  spaceId: string | null,
): T | undefined {
  const reachable = pages
    .filter((page) => page.spaceId === spaceId || page.spaceId === null)
    .sort((a, b) => Number(a.spaceId === null) - Number(b.spaceId === null));
  if (link.startsWith("wissen:")) {
    const address = pageAddress(link.slice("wissen:".length));
    return reachable.find((page) => pageAddress(page.path) === address);
  }
  const title = link.trim().toLowerCase();
  return reachable.find((page) => page.title.toLowerCase() === title);
}

/** The headings of a page, for the outline beside it. */
export function pageOutline(content: string): { level: number; text: string }[] {
  const headings: { level: number; text: string }[] = [];
  for (const line of content.split("\n")) {
    const match = /^(#{1,6})\s+(.*)$/.exec(line);
    if (!match) continue;
    headings.push({ level: match[1]?.length ?? 1, text: match[2]?.trim() ?? "" });
  }
  return headings;
}

/** A short address for a title: lower case, without umlauts, with dashes. */
export function pageSlug(title: string): string {
  const slug = title
    .trim()
    .toLowerCase()
    .replaceAll("ä", "ae")
    .replaceAll("ö", "oe")
    .replaceAll("ü", "ue")
    .replaceAll("ß", "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "seite";
}

/**
 * The address of a page: its short title. The folder and the kind are not
 * part of it, so a page keeps its address when it moves.
 */
export function pagePathFor(title: string): string {
  return pageSlug(title);
}

/**
 * The address of a page, with a number when the title is already taken. The
 * pages of a space keep their address, so a link does not break.
 */
export function uniquePagePath(title: string, taken: readonly string[]): string {
  const base = pagePathFor(title);
  if (!taken.includes(base)) return base;
  let counter = 2;
  while (taken.includes(`${base}-${counter}`)) counter += 1;
  return `${base}-${counter}`;
}

/** Addresses began with the kind once, as "handbuch/angebot"; such an address still finds its page. */
export function pageAddress(path: string): string {
  return path
    .trim()
    .replace(/^\/+/, "")
    .replace(/^(handbuch|firmenwissen)\//, "");
}

/** The first lines of a page, for the list. */
export function pageExcerpt(content: string, maxLength = 140): string {
  const text = content
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "" && !line.startsWith("#"))
    .join(" ");
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
}

/**
 * The word that follows the last open `[[` before the cursor, or null when the
 * cursor does not stand inside a wiki link. The surface uses it to offer the
 * pages that fit.
 */
export function wikiLinkQueryAt(content: string, cursor: number): string | null {
  const before = content.slice(0, Math.max(0, cursor));
  const open = before.lastIndexOf("[[");
  if (open < 0) return null;
  if (before.lastIndexOf("]]") > open) return null;
  const query = before.slice(open + 2);
  if (query.includes("\n")) return null;
  return query;
}

/** Puts a wiki link into the text and returns where the cursor belongs. */
export function insertWikiLink(
  content: string,
  cursor: number,
  title: string,
): { content: string; cursor: number } {
  const before = content.slice(0, Math.max(0, cursor));
  const open = before.lastIndexOf("[[");
  if (open < 0) return { content, cursor };
  return {
    content: `${before.slice(0, open)}[[${title}]]${content.slice(cursor)}`,
    cursor: open + title.length + 4,
  };
}

/** The title of the page where an account adds its own rules for the knowledge. */
export const RULES_PAGE_TITLE = "Wissensregeln";

/** How agents keep the knowledge in order. Every agent reads it before it writes. */
export const KNOWLEDGE_RULES = [
  "How the knowledge is kept in order:",
  "- One folder per topic, such as Konkurrenz, Kunden or Zielgruppe, named in the language of the office. Handbook and company knowledge are kinds of pages, never folders.",
  "- Use the folder that fits; look at the folders below or with wissen_ordner first. Make a new folder only for a new topic, at most two levels deep.",
  "- Every folder has an overview page titled like the folder: a short introduction and a link to every page and file in it, each with one line. Link a new page there.",
  "- One page per subject, with a clear title and a one-sentence beschreibung. Start with Stand (date) and sources. Link pages with [[Title]], never with a path.",
  "- Keep the files of a topic (sources, full texts, images) in its folder and name them on its pages.",
  "- Change a page instead of writing a second one on the same subject.",
  "- Research, results and facts about the business belong here, where people and other agents find them. Your own memory (remember) only holds how you yourself work: preferences and habits.",
  "- Files the business uses and makes (logos, photos, templates, ad creatives) belong in the assets, not here: the assets tool shows them.",
].join("\n");

/** One page in the catalog of an agent. */
export type CatalogPage = { title: string; path: string; folder: string; summary: string };

/** How many pages and files the catalog names; wissen_ordner shows the rest. */
const CATALOG_PAGES = 80;
const CATALOG_FILES = 40;

/**
 * The lines in the prompt that tell an agent how the knowledge is kept and
 * what it may read, as a tree of folders. The pages and files themselves stay
 * out of the prompt; the agent asks for the one it needs.
 */
export function formatKnowledgeCatalog(
  pages: readonly CatalogPage[],
  files: readonly { name: string; folder: string }[] = [],
): string {
  const parts = [KNOWLEDGE_RULES];
  if (pages.some((page) => page.title.toLowerCase() === RULES_PAGE_TITLE.toLowerCase())) {
    parts.push(
      `This account adds its own rules on the page [[${RULES_PAGE_TITLE}]]; read it before you write.`,
    );
  }
  if (pages.length === 0 && files.length === 0) {
    parts.push("The knowledge you may read is empty so far.");
    return parts.join("\n");
  }
  const shownPages = pages.slice(0, CATALOG_PAGES);
  const shownFiles = files.slice(0, CATALOG_FILES);
  const folders = folderTree([...shownPages, ...shownFiles].map((entry) => entry.folder));
  const lines: string[] = [];
  for (const folder of ["", ...folders]) {
    const here = shownPages.filter((page) => page.folder === folder);
    const hereFiles = shownFiles.filter((file) => file.folder === folder);
    if (folder) lines.push(`${"  ".repeat(folder.split("/").length - 1)}${folderName(folder)}/`);
    else if (here.length + hereFiles.length > 0) lines.push("(top)");
    const indent = "  ".repeat(folder ? folder.split("/").length : 1);
    for (const page of here) {
      const summary = page.summary ? `: ${page.summary.slice(0, 120)}` : "";
      lines.push(`${indent}- ${page.path} — ${page.title}${summary}`);
    }
    for (const file of hereFiles) lines.push(`${indent}- file ${file.name}`);
  }
  parts.push("The knowledge you may read, by folder (path — title: description):", ...lines);
  const hidden = pages.length - shownPages.length + files.length - shownFiles.length;
  if (hidden > 0) parts.push(`…and ${hidden} more; wissen_ordner shows every folder.`);
  parts.push(
    "Call wissen_lesen with the path of a page to read it, and follow its links when the task needs them. wissen_suchen finds pages by their words. Call wissen_datei_holen to put a file on your computer before you use it; keep a result others need with wissen_datei_ablegen.",
  );
  return parts.join("\n");
}

/** Folder names that are kinds of pages, not topics. */
const KIND_FOLDERS = new Set(["handbuch", "firmenwissen", "handbook", "company knowledge"]);

/** The name in a folder path that is a kind of page, as agents tended to make; undefined when there is none. */
export function kindFolderIn(path: string): string | undefined {
  return path.split("/").find((name) => KIND_FOLDERS.has(name.trim().toLowerCase()));
}

/** A folder name as it is compared: its slug without a plural ending, so Kunde and Kunden meet. */
function folderKey(name: string): string {
  const slug = pageSlug(name);
  return slug.length > 4 ? slug.replace(/(en|n|e|s)$/, "") : slug;
}

/**
 * The folder of a place that has almost the same path as a new one, such as
 * "Kunden" for "kunde". Undefined when the path exists already or nothing is
 * alike.
 */
export function similarFolder(path: string, existing: readonly string[]): string | undefined {
  if (existing.includes(path)) return undefined;
  const key = path.split("/").map(folderKey).join("/");
  return existing.find((other) => other.split("/").map(folderKey).join("/") === key);
}

/** Whether a page is the overview of its folder: titled like the folder, or an overview by name. */
export function isOverviewPage(page: { title: string; folder: string }): boolean {
  if (!page.folder) return false;
  const title = page.title.trim().toLowerCase();
  return (
    title === folderName(page.folder).toLowerCase() ||
    title.includes("übersicht") ||
    title.includes("overview")
  );
}

/** Whether a text links to a page, by its title or its address. */
export function linksTo(content: string, page: { title: string; path: string }): boolean {
  const links = parsePageLinks(content).map((link) => link.toLowerCase());
  return (
    links.includes(page.title.toLowerCase()) || links.includes(`wissen:${page.path}`.toLowerCase())
  );
}

/**
 * What is out of order in the knowledge of one place: what an agent fixes
 * when it tidies up. Each line names the page or folder and what to do.
 */
export function knowledgeProblems(place: {
  pages: readonly (CatalogPage & { content: string })[];
  files: readonly { name: string; folder: string }[];
  folders: readonly string[];
}): string[] {
  const { pages, files } = place;
  const problems: string[] = [];
  const folders = folderTree([
    ...place.folders,
    ...pages.map((page) => page.folder),
    ...files.map((file) => file.folder),
  ]);
  for (const folder of folders) {
    if (kindFolderIn(folderName(folder))) {
      problems.push(
        `Folder "${folder}" is named after a kind of page: move what it holds into topic folders and remove it.`,
      );
    }
  }
  for (const [index, folder] of folders.entries()) {
    const twin = similarFolder(folder, folders.slice(0, index));
    if (twin) problems.push(`Folders "${twin}" and "${folder}" hold the same topic: join them.`);
  }
  for (const folder of folders) {
    const here = pages.filter((page) => page.folder === folder);
    const hereFiles = files.filter((file) => file.folder === folder);
    const below = folders.some((other) => other.startsWith(`${folder}/`));
    if (here.length + hereFiles.length === 0) {
      if (!below) problems.push(`Folder "${folder}" is empty: remove it.`);
      continue;
    }
    if (here.length + hereFiles.length < 2) continue;
    const overview = here.find(isOverviewPage);
    if (!overview) {
      problems.push(
        `Folder "${folder}" has no overview page: write one titled "${folderName(folder)}" that links every page.`,
      );
      continue;
    }
    const missing = here.filter((page) => page !== overview && !linksTo(overview.content, page));
    if (missing.length > 0) {
      problems.push(
        `The overview "${overview.title}" does not link ${missing.map((page) => `[[${page.title}]]`).join(", ")}.`,
      );
    }
  }
  const top = pages.filter(
    (page) => !page.folder && page.title.toLowerCase() !== RULES_PAGE_TITLE.toLowerCase(),
  );
  if (top.length > 0 && folders.length > 0) {
    problems.push(
      `At the top, outside every folder: ${top.map((page) => page.path).join(", ")}. Move each into the folder of its topic.`,
    );
  }
  const seen = new Map<string, string>();
  for (const page of pages) {
    const key = page.title.trim().toLowerCase();
    const other = seen.get(key);
    if (other) problems.push(`"${page.title}" exists twice (${other}, ${page.path}): merge them.`);
    else seen.set(key, page.path);
  }
  const bare = pages.filter((page) => !page.summary.trim());
  if (bare.length > 0) {
    problems.push(
      `Without a beschreibung: ${bare.map((page) => page.path).join(", ")}. Give each one sentence.`,
    );
  }
  return problems;
}

function isControl(char: string): boolean {
  return char.charCodeAt(0) < 32;
}

/** How deep folders nest, and how long one folder name may be. */
export const MAX_FOLDER_DEPTH = 6;
export const MAX_FOLDER_NAME = 60;

/**
 * A folder path as it is stored: names joined by "/", without empty names or
 * a leading slash. Null when the path cannot be a folder. The empty path is
 * the top of a place.
 */
export function normalizeFolder(input: string): string | null {
  const names = input
    .split("/")
    .map((name) => name.trim())
    .filter((name) => name !== "");
  if (names.length > MAX_FOLDER_DEPTH) return null;
  for (const name of names) {
    if (name === "." || name === ".." || name.length > MAX_FOLDER_NAME) return null;
    if (name.includes("\\") || [...name].some(isControl)) return null;
  }
  return names.join("/");
}

/** The folders above a path and the path itself: "a/b" gives "a" and "a/b". */
export function folderAncestors(path: string): string[] {
  const names = path.split("/").filter((name) => name !== "");
  return names.map((_, index) => names.slice(0, index + 1).join("/"));
}

/** Whether a folder lies in another one or is that one. */
export function inFolder(folder: string, parent: string): boolean {
  return parent === "" || folder === parent || folder.startsWith(`${parent}/`);
}

/** The folder after its parent was renamed; unchanged when it lies elsewhere. */
export function renamedFolder(folder: string, from: string, to: string): string {
  if (folder === from) return to;
  if (folder.startsWith(`${from}/`)) return `${to}${folder.slice(from.length)}`;
  return folder;
}

/** The last name of a folder path. */
export function folderName(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/** Every folder of a place, with the folders above each, sorted. */
export function folderTree(paths: Iterable<string>): string[] {
  const all = new Set<string>();
  for (const path of paths) for (const ancestor of folderAncestors(path)) all.add(ancestor);
  return [...all].sort((a, b) => a.localeCompare(b, "de"));
}

/** A file name that is not taken in its folder yet: "logo.png" becomes "logo-2.png". */
export function uniqueFileName(name: string, taken: readonly string[]): string {
  const lower = new Set(taken.map((entry) => entry.toLowerCase()));
  if (!lower.has(name.toLowerCase())) return name;
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const extension = dot > 0 ? name.slice(dot) : "";
  let counter = 2;
  while (lower.has(`${stem}-${counter}${extension}`.toLowerCase())) counter += 1;
  return `${stem}-${counter}${extension}`;
}

/** A file name as it is stored: no folder part and no control characters. */
export function cleanFileName(input: string): string | null {
  const last = input.split(/[\\/]/).pop() ?? "";
  const name = [...last]
    .filter((char) => !isControl(char))
    .join("")
    .trim();
  if (!name || name === "." || name === "..") return null;
  return name.slice(0, 160);
}
